import React, { useState, useEffect, useRef } from "react";
import { loadStripe, type Stripe, type StripeCardElement } from "@stripe/stripe-js";
import { apiPost } from "../../api";
import { SvgIcon } from "../SvgIcon";
import { BtnSpinner } from "../BtnSpinner";

interface CardSetupResult {
  last4: string;
  brand: string;
  expiryMM: number;
  expiryYYYY: number;
}

interface SetupIntentResponse {
  clientSecret: string;
  publishableKey: string;
}

interface ConfirmResponse {
  ok: boolean;
  last4: string;
  brand: string;
  expiryMM: number;
  expiryYYYY: number;
}

export function CardSetupModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: CardSetupResult) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const cardElementRef = useRef<StripeCardElement | null>(null);
  const cardMountRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!props.isOpen) {
      // Reset state when closed
      setLoading(true);
      setSubmitting(false);
      setError(null);
      setStripe(null);
      setClientSecret(null);
      cardElementRef.current = null;
      mountedRef.current = false;
      return;
    }

    let cancelled = false;

    async function init() {
      setLoading(true);
      setError(null);

      // Fetch SetupIntent from backend
      const res = await apiPost<SetupIntentResponse>("/v1/account/recovery/card/setup-intent", {});
      if (cancelled) return;

      if (!res.ok) {
        setError(res.error || "Failed to initialize card setup");
        setLoading(false);
        return;
      }

      // Determine publishable key: prefer env var, fall back to backend response
      const pk = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || res.data.publishableKey;
      if (!pk || pk === "pk_test_your_key_here") {
        setError("Stripe is not configured. Contact your administrator.");
        setLoading(false);
        return;
      }

      const stripeInstance = await loadStripe(pk);
      if (cancelled) return;

      if (!stripeInstance) {
        setError("Failed to load Stripe");
        setLoading(false);
        return;
      }

      setStripe(stripeInstance);
      setClientSecret(res.data.clientSecret);
      setLoading(false);
    }

    init();
    return () => { cancelled = true; };
  }, [props.isOpen]);

  // Mount Stripe CardElement when stripe is ready and the DOM node is available
  useEffect(() => {
    if (!stripe || !cardMountRef.current || mountedRef.current) return;
    mountedRef.current = true;

    const elements = stripe.elements();
    const cardElement = elements.create("card", {
      style: {
        base: {
          fontSize: "14px",
          color: "#e0e0e0",
          "::placeholder": { color: "#888" },
          fontFamily: "system-ui, -apple-system, sans-serif"
        },
        invalid: { color: "#ef4444" }
      }
    });
    cardElement.mount(cardMountRef.current);
    cardElementRef.current = cardElement;

    return () => {
      cardElement.unmount();
      mountedRef.current = false;
    };
  }, [stripe]);

  async function handleSubmit() {
    if (!stripe || !clientSecret || !cardElementRef.current) return;

    setSubmitting(true);
    setError(null);

    // Confirm the SetupIntent with the card element
    const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: cardElementRef.current }
    });

    if (stripeError) {
      setError(stripeError.message || "Card verification failed");
      setSubmitting(false);
      return;
    }

    if (!setupIntent || setupIntent.status !== "succeeded") {
      setError("Card setup did not complete");
      setSubmitting(false);
      return;
    }

    // Confirm with backend — runs $0 auth and stores card details
    const confirmRes = await apiPost<ConfirmResponse>("/v1/account/recovery/card/confirm", {
      setupIntentId: setupIntent.id
    });

    if (!confirmRes.ok) {
      setError(confirmRes.error || "Failed to verify card");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    props.onSuccess({
      last4: confirmRes.data.last4,
      brand: confirmRes.data.brand,
      expiryMM: confirmRes.data.expiryMM,
      expiryYYYY: confirmRes.data.expiryYYYY
    });
    props.onClose();
  }

  if (!props.isOpen) return null;

  return (
    <div className="modalBackdrop" role="dialog" aria-modal="true">
      <div className="modalCard" style={{ width: "min(480px, 92vw)" }}>
        <div className="modalHeader">
          <div className="modalTitle">Add recovery card</div>
          <button className="iconBtn" onClick={props.onClose} title="Close" disabled={submitting}>
            <SvgIcon name="x-circle" title="Close" />
          </button>
        </div>

        <div className="modalBody" style={{ padding: "16px 20px" }}>
          {loading ? (
            <div style={{ fontSize: "13px", color: "var(--muted)", textAlign: "center", padding: "24px 0" }}>
              Initializing card setup…
            </div>
          ) : (
            <>
              <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "16px", lineHeight: "1.5" }}>
                Add a credit or debit card as a recovery anchor. A $0 authorization will verify your card — no charge will be made.
              </div>

              <div
                ref={cardMountRef}
                style={{
                  padding: "12px",
                  border: "1px solid var(--control-border)",
                  borderRadius: "6px",
                  background: "var(--surface)",
                  minHeight: "20px"
                }}
              />

              {error && (
                <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "12px" }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modalFooter" style={{ padding: "12px 20px" }}>
          <button className="btn" onClick={props.onClose} disabled={submitting}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn primary"
            onClick={handleSubmit}
            disabled={loading || submitting || !stripe}
          >
            <BtnSpinner loading={submitting}>{submitting ? "Verifying…" : "Verify card"}</BtnSpinner>
          </button>
        </div>
      </div>
    </div>
  );
}
